import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { scrollToTop } from "../formHelpers";
import { findDefaultObligee } from "../obligeeInformationConfig";

const normalizeId = (v) => (v === null || v === undefined ? "" : String(v));

const matchObligeeId = (obligeeId, candidateId, useNormalize) => {
  if (useNormalize) {
    return normalizeId(obligeeId) === normalizeId(candidateId);
  }
  return obligeeId === candidateId;
};

const findObligeeById = (obligees, obligeeId, useNormalize) =>
  obligees.find((o) => matchObligeeId(obligeeId, o.obligee_id, useNormalize));

function applyDelayedDefaultWithRef({
  suppressDefaultObligee,
  hasSeenObligeeFromParentRef,
  dataRef,
  obligeesRes,
  bondType,
  updateData,
}) {
  if (suppressDefaultObligee) return;

  const setDefaultIfStillUnset = () => {
    if (hasSeenObligeeFromParentRef.current) return;
    const current = dataRef.current;
    if (current?.obligeeId && String(current.obligeeId).trim() !== "") return;
    const defaultObligee = findDefaultObligee(obligeesRes, bondType);
    if (defaultObligee) {
      updateData({ ...current, obligeeId: defaultObligee.obligee_id });
    }
  };
  setTimeout(setDefaultIfStillUnset, 100);
}

function applyImmediateUnlessSuppressed({
  suppressDefaultObligee,
  cfg,
  dataRef,
  obligeesRes,
  bondType,
  updateData,
}) {
  if (suppressDefaultObligee) {
    if (cfg.debugRetrieveLogs) {
      console.log(
        "[ObligeeInformationForm retrieve] Skipping default obligee (retrieve page); parent supplies obligee from API."
      );
    }
    return true;
  }

  const current = dataRef.current;
  if (!current?.obligeeId || String(current.obligeeId).trim() === "") {
    const defaultObligee = findDefaultObligee(obligeesRes, bondType);
    if (defaultObligee) {
      console.log(
        "[ObligeeInformationForm create] Setting default obligee:",
        defaultObligee.obligee_id,
        defaultObligee.obligee_name
      );
      updateData({ ...current, obligeeId: defaultObligee.obligee_id });
    }
  }
  return false;
}

function applyImmediateOnMount({ data, obligeesRes, bondType, updateData }) {
  if (!data.obligeeId) {
    const defaultObligee = findDefaultObligee(obligeesRes, bondType);
    if (defaultObligee) {
      updateData({ ...data, obligeeId: defaultObligee.obligee_id });
    }
  }
}

function applyObligeeDefaultStrategy(strategy, ctx) {
  switch (strategy) {
    case "delayedWithRef":
      applyDelayedDefaultWithRef(ctx);
      return false;
    case "immediateUnlessSuppressed":
      return applyImmediateUnlessSuppressed(ctx);
    case "immediateOnMount":
      applyImmediateOnMount(ctx);
      return false;
    default:
      return false;
  }
}

const EMPTY_NEW_OBLIGEE = { name: "", street: "", city: "", state: "", zip: "" };

const SelectedObligeeDetails = ({ selected }) => {
  if (!selected) return null;

  return (
    <>
      <Typography sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: "bold" }}>
          Name:
        </Box>{" "}
        {selected.obligee_name}
      </Typography>
      <Typography sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: "bold" }}>
          Address:
        </Box>{" "}
        {selected.obligee_address}
      </Typography>
      <Typography sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: "bold" }}>
          City:
        </Box>{" "}
        {selected.obligee_city}
      </Typography>
      <Typography sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: "bold" }}>
          State:
        </Box>{" "}
        {selected.obligee_state}
      </Typography>
      <Typography>
        <Box component="span" sx={{ fontWeight: "bold" }}>
          ZIP:
        </Box>{" "}
        {selected.obligee_zip}
      </Typography>
    </>
  );
};

SelectedObligeeDetails.propTypes = {
  selected: PropTypes.object,
};

const AddObligeeDialog = ({
  open,
  onClose,
  newObligee,
  setNewObligee,
  allCities,
  allStates,
  onSubmit,
  GoogleAutoComplete,
  showGoogleAutocomplete,
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Add New Obligee</DialogTitle>
    <DialogContent>
      <TextField
        label="Obligee Name"
        value={newObligee.name}
        onChange={(e) => setNewObligee({ ...newObligee, name: e.target.value })}
        fullWidth
        margin="normal"
        required
        sx={showGoogleAutocomplete ? { mb: 2 } : undefined}
      />

      {showGoogleAutocomplete && GoogleAutoComplete ? (
        <GoogleAutoComplete placeholder="Search Address" callback={onSubmit.onGoogleCallback} />
      ) : null}

      <TextField
        label="Street Address"
        value={newObligee.street}
        onChange={(e) => setNewObligee({ ...newObligee, street: e.target.value })}
        fullWidth
        margin="normal"
        required
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
          options={allCities}
          value={newObligee.city}
          onChange={(e, value) => setNewObligee({ ...newObligee, city: value })}
          renderInput={(params) => (
            <TextField {...params} label="City" fullWidth required />
          )}
          sx={{ flex: 1 }}
        />

        <Autocomplete
          options={allStates}
          value={newObligee.state}
          onChange={(e, value) => setNewObligee({ ...newObligee, state: value })}
          renderInput={(params) => (
            <TextField {...params} label="State" fullWidth required />
          )}
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        label="ZIP Code"
        value={newObligee.zip}
        onChange={(e) => setNewObligee({ ...newObligee, zip: e.target.value })}
        fullWidth
        margin="normal"
        required
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onSubmit.onAdd}
        variant="contained"
        disabled={
          !newObligee.name ||
          !newObligee.street ||
          !newObligee.city ||
          !newObligee.state ||
          !newObligee.zip
        }
      >
        Add Obligee
      </Button>
    </DialogActions>
  </Dialog>
);

AddObligeeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  newObligee: PropTypes.object.isRequired,
  setNewObligee: PropTypes.func.isRequired,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
  onSubmit: PropTypes.shape({
    onAdd: PropTypes.func.isRequired,
    onGoogleCallback: PropTypes.func,
  }).isRequired,
  GoogleAutoComplete: PropTypes.elementType,
  showGoogleAutocomplete: PropTypes.bool,
};

const ObligeeAutocompleteField = ({
  obligees,
  loadingObligees,
  value,
  onChange,
  isDisabled,
  showValidationErrors,
  errors,
  normalizeIdMatching,
}) => (
  <Autocomplete
    disabled={isDisabled}
    options={obligees}
    getOptionLabel={(option) => option.obligee_name || ""}
    loading={loadingObligees}
    value={value}
    isOptionEqualToValue={(option, selected) =>
      matchObligeeId(option?.obligee_id, selected?.obligee_id, normalizeIdMatching)
    }
    onChange={(event, newValue) => {
      onChange(newValue ? newValue.obligee_id : null);
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        disabled={isDisabled}
        label="Select Obligee"
        variant="outlined"
        error={showValidationErrors ? !!errors?.obligeeId : false}
        helperText={showValidationErrors ? errors?.obligeeId : undefined}
        fullWidth
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingObligees ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    )}
  />
);

ObligeeAutocompleteField.propTypes = {
  obligees: PropTypes.array,
  loadingObligees: PropTypes.bool,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
  errors: PropTypes.object,
  normalizeIdMatching: PropTypes.bool,
};

const BondObligeeInformationBase = ({
  cfg,
  GoogleAutoComplete,
  data,
  updateData,
  globalData,
  errors = {},
  bondType,
  suppressDefaultObligee = false,
  isDisabled = false,
  control,
  watch,
  obligees: obligeesProp,
  loadingObligees: loadingObligeesProp = false,
  openObligeeDialog: openObligeeDialogProp,
  setOpenObligeeDialog: setOpenObligeeDialogProp,
  newObligee: newObligeeProp,
  setNewObligee: setNewObligeeProp,
  handleAddObligee: handleAddObligeeProp,
  handleObligeeGoogleCallback: handleObligeeGoogleCallbackProp,
  allCities: allCitiesProp = [],
  allStates: allStatesProp = [],
}) => {
  if (cfg.mode === "rhf") {
    return (
      <RhfObligeeInformation
        cfg={cfg}
        GoogleAutoComplete={GoogleAutoComplete}
        control={control}
        watch={watch}
        obligees={obligeesProp}
        loadingObligees={loadingObligeesProp}
        openObligeeDialog={openObligeeDialogProp}
        setOpenObligeeDialog={setOpenObligeeDialogProp}
        newObligee={newObligeeProp}
        setNewObligee={setNewObligeeProp}
        handleAddObligee={handleAddObligeeProp}
        handleObligeeGoogleCallback={handleObligeeGoogleCallbackProp}
        allCities={allCitiesProp}
        allStates={allStatesProp}
        isDisabled={isDisabled}
      />
    );
  }

  return (
    <GlobalStateObligeeInformation
      cfg={cfg}
      GoogleAutoComplete={GoogleAutoComplete}
      data={data}
      updateData={updateData}
      globalData={globalData}
      errors={errors}
      bondType={bondType}
      suppressDefaultObligee={suppressDefaultObligee}
      isDisabled={isDisabled}
    />
  );
};

const GlobalStateObligeeInformation = ({
  cfg,
  GoogleAutoComplete,
  data,
  updateData,
  errors,
  bondType,
  suppressDefaultObligee,
  isDisabled,
}) => {
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const [newObligee, setNewObligee] = useState(EMPTY_NEW_OBLIGEE);
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const dataRef = useRef(data);
  dataRef.current = data;
  const hasSeenObligeeFromParentRef = useRef(false);

  if (data?.obligeeId != null && String(data.obligeeId).trim() !== "") {
    hasSeenObligeeFromParentRef.current = true;
  }

  const selectedObligee = findObligeeById(
    obligees,
    data?.obligeeId,
    cfg.normalizeId
  );

  if (typeof window !== "undefined" && cfg.debugRetrieveLogs && suppressDefaultObligee) {
    console.log(
      "[ObligeeInformationForm retrieve] suppressDefaultObligee=true, data.obligeeId=",
      data?.obligeeId,
      "data=",
      data
    );
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoadingObligees(true);
      try {
        const { apiClient } = require("../../../../api/httpClient");
        const [obligeesRes, citiesRes, statesRes] = await Promise.all([
          apiClient("/obligee-list-create/"),
          apiClient("/fields/city/"),
          apiClient("/fields/states/"),
        ]);

        setObligees(obligeesRes);
        setAllCities(citiesRes);
        setAllStates(statesRes);

        const shouldStop = applyObligeeDefaultStrategy(cfg.defaultStrategy, {
          suppressDefaultObligee,
          hasSeenObligeeFromParentRef,
          dataRef,
          obligeesRes,
          bondType,
          updateData,
          data,
          cfg,
        });
        if (shouldStop) return;
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load obligee data");
      } finally {
        setLoadingObligees(false);
      }
    };

    fetchData();
    if (cfg.scrollToTopOnMount) {
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleCallback = (address, city, state, zip) => {
    setNewObligee((prev) => ({
      ...prev,
      street: address,
      city:
        allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
      state:
        allStates.find((s) => s.toLowerCase() === state.toLowerCase()) || state,
      zip,
    }));
  };

  const handleAddObligee = async () => {
    if (cfg.blockFloridaDuplicate) {
      const isFlorida =
        (newObligee.state || "").toLowerCase() === "florida" ||
        (newObligee.state || "").toUpperCase() === "FL";
      if (isFlorida) {
        const floridaExists = obligees.some(
          (o) =>
            (o.obligee_state || "").toLowerCase() === "florida" ||
            (o.obligee_state || "").toUpperCase() === "FL"
        );
        if (floridaExists) {
          toast.info(
            'A Florida obligee already exists. Please use the existing "Governor of Florida (Ron Desantis)".'
          );
          return;
        }
      }
    }

    try {
      setLoadingObligees(true);
      const { apiClient } = require("../../../../api/httpClient");
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });

      setObligees((prev) => [...prev, response]);
      updateData({ ...data, obligeeId: response.obligee_id });
      setOpenObligeeDialog(false);
      setNewObligee(EMPTY_NEW_OBLIGEE);
      toast.success("Obligee added successfully!");
    } catch (error) {
      console.error("Error adding obligee:", error);
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  };

  const content = (
  <>
    <Typography variant="h5" className="font-bold mb-4">
      Obligee Information
    </Typography>

    <Box className="grid grid-cols-1 gap-4">
      <ObligeeAutocompleteField
        obligees={obligees}
        loadingObligees={loadingObligees}
        value={selectedObligee || null}
        onChange={(obligeeId) => updateData({ ...data, obligeeId })}
        isDisabled={isDisabled}
        showValidationErrors={cfg.showValidationErrors}
        errors={errors}
        normalizeIdMatching={cfg.normalizeId}
      />

      <Button
        variant="outlined"
        onClick={() => setOpenObligeeDialog(true)}
        disabled={loadingObligees || isDisabled}
      >
        {loadingObligees ? <CircularProgress size={24} /> : "Add New Obligee"}
      </Button>

      {data?.obligeeId ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            Selected Obligee:
          </Typography>
          <SelectedObligeeDetails selected={selectedObligee} />
        </Box>
      ) : null}
    </Box>

    <AddObligeeDialog
      open={openObligeeDialog}
      onClose={() => setOpenObligeeDialog(false)}
      newObligee={newObligee}
      setNewObligee={setNewObligee}
      allCities={allCities}
      allStates={allStates}
      GoogleAutoComplete={GoogleAutoComplete}
      showGoogleAutocomplete={cfg.showGoogleAutocomplete}
      onSubmit={{
        onAdd: handleAddObligee,
        onGoogleCallback: handleGoogleCallback,
      }}
    />
  </>
  );

  if (cfg.showOuterCard) {
    return (
      <Box
        className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
        sx={{ border: "1px solid #e0e0e0" }}
      >
        {content}
      </Box>
    );
  }

  return content;
};

GlobalStateObligeeInformation.propTypes = {
  cfg: PropTypes.object.isRequired,
  GoogleAutoComplete: PropTypes.elementType,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  bondType: PropTypes.string,
  suppressDefaultObligee: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

const RhfObligeeInformation = ({
  cfg,
  GoogleAutoComplete,
  control,
  watch,
  obligees = [],
  loadingObligees,
  openObligeeDialog,
  setOpenObligeeDialog,
  newObligee,
  setNewObligee,
  handleAddObligee,
  handleObligeeGoogleCallback,
  allCities,
  allStates,
  isDisabled,
}) => {
  const selectedId = watch("obligeeId");
  const selectedObligee = obligees.find((o) => o.obligee_id === selectedId);

  const autocomplete = (
    <Controller
      name="obligeeId"
      control={control}
      render={({ field }) => (
        <ObligeeAutocompleteField
          obligees={obligees}
          loadingObligees={loadingObligees}
          value={obligees.find((o) => o.obligee_id === field.value) || null}
          onChange={field.onChange}
          isDisabled={isDisabled}
          showValidationErrors={false}
          normalizeIdMatching={false}
        />
      )}
    />
  );

  const addButton = (
    <Button
      variant="outlined"
      onClick={() => setOpenObligeeDialog(true)}
      disabled={loadingObligees}
      fullWidth={cfg.fullWidthAddButton}
    >
      {loadingObligees ? <CircularProgress size={24} /> : "Add New Obligee"}
    </Button>
  );

  const selectedDetails = selectedId ? (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Selected Obligee:
      </Typography>
      <SelectedObligeeDetails selected={selectedObligee} />
    </Box>
  ) : null;

  return (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Obligee Information
      </Typography>

      {cfg.useGridLayout ? (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>{autocomplete}</Grid>
          {cfg.hideAddWhenDisabled && isDisabled ? null : (
            <Grid size={{ xs: 12 }}>{addButton}</Grid>
          )}
          {selectedId ? <Grid size={{ xs: 12 }}>{selectedDetails}</Grid> : null}
        </Grid>
      ) : (
        <>
          {autocomplete}
          {cfg.hideAddWhenDisabled && isDisabled ? null : addButton}
          {selectedDetails}
        </>
      )}

      <AddObligeeDialog
        open={openObligeeDialog}
        onClose={() => setOpenObligeeDialog(false)}
        newObligee={newObligee}
        setNewObligee={setNewObligee}
        allCities={allCities}
        allStates={allStates}
        GoogleAutoComplete={GoogleAutoComplete}
        showGoogleAutocomplete={cfg.showGoogleAutocomplete}
        onSubmit={{
          onAdd: handleAddObligee,
          onGoogleCallback: handleObligeeGoogleCallback,
        }}
      />
    </Box>
  );
};

RhfObligeeInformation.propTypes = {
  cfg: PropTypes.object.isRequired,
  GoogleAutoComplete: PropTypes.elementType,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  obligees: PropTypes.array,
  loadingObligees: PropTypes.bool,
  openObligeeDialog: PropTypes.bool,
  setOpenObligeeDialog: PropTypes.func.isRequired,
  newObligee: PropTypes.object,
  setNewObligee: PropTypes.func.isRequired,
  handleAddObligee: PropTypes.func.isRequired,
  handleObligeeGoogleCallback: PropTypes.func.isRequired,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
  isDisabled: PropTypes.bool,
};

BondObligeeInformationBase.propTypes = {
  cfg: PropTypes.object.isRequired,
  GoogleAutoComplete: PropTypes.elementType,
  data: PropTypes.object,
  updateData: PropTypes.func,
  globalData: PropTypes.object,
  errors: PropTypes.object,
  bondType: PropTypes.string,
  suppressDefaultObligee: PropTypes.bool,
  isDisabled: PropTypes.bool,
  control: PropTypes.object,
  watch: PropTypes.func,
  obligees: PropTypes.array,
  loadingObligees: PropTypes.bool,
  openObligeeDialog: PropTypes.bool,
  setOpenObligeeDialog: PropTypes.func,
  newObligee: PropTypes.object,
  setNewObligee: PropTypes.func,
  handleAddObligee: PropTypes.func,
  handleObligeeGoogleCallback: PropTypes.func,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
};

export default BondObligeeInformationBase;
